import { useRef, useState, useCallback } from "react";

const Slider = ({ before, after, altBefore, altAfter, testid, beforeLabel = "Before", afterLabel = "After" }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const setFromClientX = useCallback((clientX) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(96, Math.max(4, pct)));
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setFromClientX(e.clientX);
  };
  const onPointerMove = (e) => {
    if (dragging.current) setFromClientX(e.clientX);
  };
  const stop = () => {
    dragging.current = false;
  };

  return (
    <div
      ref={ref}
      data-testid={testid}
      className="relative aspect-[4/3] overflow-hidden border border-[#2d4a22]/20 select-none touch-none cursor-ew-resize bg-[#e4dacb]"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={stop}
      onPointerLeave={stop}
    >
      <img src={after} alt={altAfter} loading="lazy" draggable="false" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
        <img
          src={before}
          alt={altBefore}
          loading="lazy"
          draggable="false"
          className="absolute inset-0 h-full object-cover"
          style={{ width: ref.current ? ref.current.getBoundingClientRect().width : "100%" }}
        />
      </div>

      <span className="absolute top-3 left-3 px-2 py-1 bg-[#1f281e]/80 text-[#faf8f5] text-[10px] uppercase tracking-widest font-bold">
        {beforeLabel}
      </span>
      <span className="absolute top-3 right-3 px-2 py-1 bg-[#2d4a22]/90 text-[#faf8f5] text-[10px] uppercase tracking-widest font-bold">
        {afterLabel}
      </span>

      <div className="absolute top-0 bottom-0 w-0.5 bg-[#faf8f5] shadow" style={{ left: `${pos}%` }}>
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-[#faf8f5] border-2 border-[#2d4a22] flex items-center justify-center shadow-md">
          <svg className="w-5 h-5 text-[#2d4a22]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="9 18 3 12 9 6" />
            <polyline points="15 6 21 12 15 18" />
          </svg>
        </div>
      </div>
    </div>
  );
};

export const BeforeAfter = () => (
  <section id="transformations" data-testid="before-after-section" className="py-24 md:py-32 bg-[#f2ece1] text-[#1f281e] border-b border-[#2d4a22]/10">
    <div className="max-w-6xl mx-auto px-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-6 gap-6">
        <div className="reveal">
          <span className="font-mono-type text-xs uppercase tracking-[0.18em] text-[#2d4a22] font-semibold block mb-2">
            Drag The Handle
          </span>
          <h2 className="font-display text-3xl md:text-5xl uppercase text-[#1f281e]">See The Transformation</h2>
        </div>
        <p className="reveal reveal-d1 text-sm text-[#3d4b3b] max-w-sm">
          From worn-out ground to a yard that turns heads — this is what one crew, start to finish, looks like.
        </p>
      </div>

      <p className="reveal text-[11px] uppercase tracking-widest text-[#8a8272] mb-10" data-testid="before-after-disclaimer">
        Turf slider: our own crew on a real Greater LA job. Garden slider: sample photos until more of our project photos arrive.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="reveal reveal-d1">
          <Slider
            testid="slider-lawn"
            before="/images/photo_4.jpg"
            after="/images/photo_1.jpg"
            altBefore="Sahagun Landscape crew installing artificial turf around a tree"
            altAfter="Finished artificial turf commercial courtyard by Sahagun Landscape"
            beforeLabel="During Install"
            afterLabel="Completed"
          />
          <p className="mt-3 text-xs uppercase tracking-widest text-[#3d4b3b] font-semibold">Artificial Turf — Commercial Courtyard</p>
        </div>
        <div className="reveal reveal-d2">
          <Slider
            testid="slider-garden"
            before="https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1200&q=80"
            after="https://images.unsplash.com/photo-1591857177580-dc82b9ac4e1e?w=1200&q=80"
            altBefore="Overgrown open field before landscaping"
            altAfter="Lush planted garden beds after landscape design"
          />
          <p className="mt-3 text-xs uppercase tracking-widest text-[#3d4b3b] font-semibold">Landscape Design &amp; Gardening</p>
        </div>
      </div>
    </div>
  </section>
);
